import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../api/client";
import ConfirmModal from "../../components/ConfirmModal";
import MapPicker from "./MapPicker";
import { Field, CoordsFields } from "./CartoDAEAdmin";

const CATEGORIES = [
  { v: "public", l: "Services publics" },
  { v: "commerce", l: "Commerce / service" },
  { v: "sport", l: "Sport / aire de jeux" },
  { v: "tri", l: "Apport volontaire (tri)" },
  { v: "mobilier", l: "Mobilier urbain" },
];

const SOUS_TYPES = {
  public: ["Mairie", "École", "Bibliothèque", "Salle des fêtes",
           "Cimetière", "Poste", "Église / culte", "Local technique",
           "Pompiers", "Gendarmerie", "Autre"],
  commerce: ["Boulangerie", "Épicerie", "Restaurant", "Café", "Médecin",
             "Infirmier", "Kiné", "Pharmacie", "Coiffeur", "Garage", "Autre"],
  sport: ["Terrain de foot", "City-stade", "Aire de jeux", "Pétanque",
          "Parcours santé", "Tennis", "Autre"],
  tri: ["Colonne verre", "Colonne textile", "Conteneur papier",
        "Borne déchèterie", "Compostage collectif", "Autre"],
  mobilier: ["Banc", "Fontaine", "Abribus", "Toilettes publiques",
             "Borne électrique", "Poubelle publique", "Autre"],
};

const catLabel = (c) => (CATEGORIES.find((x) => x.v === c) || { l: c }).l;

export default function CartoLieuxAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/admin/cartographie/lieux")).items || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    try { await api.del(`/admin/cartographie/lieux/${toDelete.id}`);
          setToDelete(null); load();
    } catch (e) { alert("Erreur : " + e.message); }
  };

  const blank = { nom: "", categorie: "public", sous_type: "", adresse: "",
                  description: "", horaires: "", telephone: "", url: "",
                  latitude: 48.1102, longitude: 0.0815 };

  const filtered = filter === "all" ? items : items.filter((i) => i.categorie === filter);

  return (
    <div>
      <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={() => setEditing(blank)}>+ Ajouter un lieu</button>
        <div style={{ marginLeft: "auto" }}>
          <label className="muted" style={{ marginRight: 8 }}>Filtrer :</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}
                  style={{ width: "auto", display: "inline-block" }}>
            <option value="all">Toutes catégories ({items.length})</option>
            {CATEGORIES.map((c) => (
              <option key={c.v} value={c.v}>
                {c.l} ({items.filter((i) => i.categorie === c.v).length})
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && <p className="muted mt-2">Chargement…</p>}
      {!loading && filtered.length === 0 &&
        <p className="muted mt-2">Aucun lieu dans cette catégorie.</p>}
      {filtered.length > 0 && (
        <table className="admin-table mt-2"><thead><tr>
          <th>Nom</th><th>Catégorie</th><th>Sous-type</th>
          <th>Coordonnées</th><th>Actions</th>
        </tr></thead><tbody>
          {filtered.map((l) => (
            <tr key={l.id}>
              <td>{l.nom}</td>
              <td className="muted">{catLabel(l.categorie)}</td>
              <td className="muted">{l.sous_type}</td>
              <td className="muted" style={{ fontFamily: "monospace", fontSize: "0.85em" }}>
                {l.latitude.toFixed(5)}, {l.longitude.toFixed(5)}
              </td>
              <td>
                <button className="small" onClick={() => setEditing(l)}>Modifier</button>
                <button className="small danger" style={{ marginLeft: 6 }}
                        onClick={() => setToDelete(l)}>Supprimer</button>
              </td>
            </tr>
          ))}
        </tbody></table>
      )}

      {editing && (
        <Editor item={editing}
                onClose={() => setEditing(null)}
                onSaved={() => { setEditing(null); load(); }} />
      )}
      {toDelete && (
        <ConfirmModal title="Supprimer ce lieu ?"
                      message={`« ${toDelete.nom} » sera définitivement supprimé.`}
                      confirmLabel="Supprimer" variant="danger"
                      onConfirm={confirmDelete} onCancel={() => setToDelete(null)} />
      )}
    </div>
  );
}

function Editor({ item, onClose, onSaved }) {
  const [form, setForm] = useState(item);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!form.nom.trim()) { setError("Le nom est obligatoire."); return; }
    setBusy(true);
    try {
      const payload = { ...form, nom: form.nom.trim() };
      if (item.id) await api.put(`/admin/cartographie/lieux/${item.id}`, payload);
      else await api.post("/admin/cartographie/lieux", payload);
      onSaved();
    } catch (e) { setError(e.message || "Erreur"); }
    finally { setBusy(false); }
  };

  const sousTypes = SOUS_TYPES[form.categorie] || [];

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 920, width: "95vw" }}>
        <div className="modal-header">
          <h3>{item.id ? "Modifier" : "Ajouter"} un lieu</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <MapPicker latitude={form.latitude} longitude={form.longitude}
                     onChange={(ll) => setForm((f) => ({ ...f, ...ll }))} />
          <CoordsFields form={form} setForm={setForm} />

          <div className="field"><label>Catégorie</label>
            <select value={form.categorie}
                    onChange={(e) => setForm({ ...form, categorie: e.target.value, sous_type: "" })}>
              {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select></div>

          <div className="field"><label>Sous-type</label>
            <select value={form.sous_type || ""}
                    onChange={(e) => setForm({ ...form, sous_type: e.target.value })}>
              <option value="">(non précisé)</option>
              {sousTypes.map((s) => <option key={s} value={s}>{s}</option>)}
            </select></div>

          <Field label="Nom" value={form.nom}
                 onChange={(v) => setForm({ ...form, nom: v })} />
          <Field label="Adresse" value={form.adresse}
                 onChange={(v) => setForm({ ...form, adresse: v })} />
          <Field label="Horaires" value={form.horaires}
                 onChange={(v) => setForm({ ...form, horaires: v })} />
          <Field label="Téléphone" value={form.telephone}
                 onChange={(v) => setForm({ ...form, telephone: v })} />
          <Field label="Site web (URL)" value={form.url}
                 onChange={(v) => setForm({ ...form, url: v })} />
          <div className="field"><label>Description</label>
            <textarea rows={3} value={form.description || ""}
                      onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>

          {error && <div className="error mt-2">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="secondary" onClick={onClose}>Annuler</button>
          <button onClick={submit} disabled={busy}>
            {busy ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
